import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Box, CircularProgress, Typography } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import sh from "bundles/common/utils/sh";
import DateRangeSelect from "../common/DateRangeSelect";
import InternalUserDrawer from "../common/InternalUserDrawer";
import Podium from "./Podium";
import ChampionsWall from "./ChampionsWall";
import TugOfWar from "./TugOfWar";
import InternalUsersTable from "./InternalUsersTable";
import UserChartsSection from "../common/user_charts/UserChartsSection";

const TABS = [
  {
    key: "hall_of_fame",
    label: "Hall of Fame",
    endpoint: "/internal/talent_rankings/hall_of_fame",
    defaultPeriod: "all_time",
    periods: [
      { value: "yearly", label: "This Year" },
      { value: "all_time", label: "All Time" },
    ],
  },
  {
    key: "top_officer",
    label: "Top Talent Officer",
    endpoint: "/internal/talent_rankings/top_officer",
    defaultPeriod: "monthly",
    periods: [
      { value: "daily", label: "Today" },
      { value: "weekly", label: "Weekly" },
      { value: "monthly", label: "Monthly" },
    ],
  },
  {
    key: "progress",
    label: "My Progress",
    endpoint: "/internal/talent_rankings/progress",
    defaultPeriod: "monthly",
    periods: [
      { value: "daily", label: "Today" },
      { value: "weekly", label: "Week" },
      { value: "monthly", label: "Month" },
      { value: "custom", label: "Custom" },
    ],
  },
];

const PERIOD_LABELS = { daily: "today", weekly: "this week", monthly: "this month", yearly: "this year", all_time: "all time" };

const SegmentedToggle = ({ options, value, onChange, reduce }) => (
  <Box sx={{ display: "flex", backgroundColor: "hsl(140, 8%, 90%)", borderRadius: "8px", p: "2px", border: "1px solid hsl(140, 8%, 88%)" }}>
    {options.map((option) => {
      const selected = value === option.value;
      return (
        <Box
          key={option.value}
          component="button"
          onClick={() => onChange(option.value)}
          sx={{
            all: "unset",
            position: "relative",
            px: "16px",
            py: "6px",
            fontSize: 12,
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            borderRadius: "6px",
            cursor: "pointer",
            transition: "color 150ms ease",
            color: selected ? "hsl(150, 30%, 12%)" : "hsl(150, 10%, 46%)",
            "&:hover": { color: "hsl(150, 30%, 12%)" },
          }}
        >
          {selected && (
            <Box
              component={motion.div}
              layoutId="tr-period-pill"
              transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 500, damping: 40 }}
              sx={{
                position: "absolute",
                inset: 0,
                backgroundColor: "#ffffff",
                borderRadius: "6px",
                boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                zIndex: 0,
              }}
            />
          )}
          <Box component="span" sx={{ position: "relative", zIndex: 1 }}>
            {option.label}
          </Box>
        </Box>
      );
    })}
  </Box>
);

const SectionLabel = ({ children }) => (
  <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150, 30%, 12%)", mb: "14px" }}>
    {children}
  </Typography>
);

const TalentRankingsPage = () => {
  const currentInternalUser = useSelector((state) => state.current_internal_user);
  const currentUserId = currentInternalUser?.id ?? null;
  const reduce = useReducedMotion();

  const [activeTabKey, setActiveTabKey] = useState("hall_of_fame");
  const [period, setPeriod] = useState("all_time");
  const [customRange, setCustomRange] = useState({ startDate: null, endDate: null });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const activeTab = useMemo(() => TABS.find((t) => t.key === activeTabKey), [activeTabKey]);

  const containerVariants = useMemo(
    () => ({ hidden: {}, show: { transition: { staggerChildren: reduce ? 0 : 0.09 } } }),
    [reduce],
  );
  const sectionVariants = useMemo(
    () => ({
      hidden: { opacity: 0, y: reduce ? 0 : 12 },
      show: { opacity: 1, y: 0, transition: { duration: reduce ? 0 : 0.4, ease: "easeOut" } },
    }),
    [reduce],
  );

  const selectTab = useCallback((tab) => {
    setActiveTabKey(tab.key);
    setPeriod(tab.defaultPeriod);
    setData(null);
    setLoading(true);
  }, []);

  const fetchData = useCallback(() => {
    if (!activeTab.endpoint) {
      setLoading(false);
      return;
    }
    const params = { period };
    if (period === "custom" && customRange.startDate && customRange.endDate) {
      params.start_date = customRange.startDate;
      params.end_date = customRange.endDate;
    }
    setLoading(true);
    sh.get(activeTab.endpoint, { params })
      .then((res) => setData(res.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [activeTab, period, customRange]);

  useEffect(() => {
    if (period === "custom" && (!customRange.startDate || !customRange.endDate)) return;
    fetchData();
  }, [fetchData, period, customRange]);

  const leaderboardRows = data?.leaderboard || data?.users || [];

  return (
    <Box sx={{ minHeight: "100vh", width: "100%", backgroundColor: "hsl(140, 10%, 95%)" }}>
      <Box sx={{ px: "32px", py: "32px", maxWidth: 1180, mx: "auto", width: "100%", display: "flex", flexDirection: "column", gap: "24px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography component="h1" sx={{ fontSize: 28, fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(150, 30%, 12%)", lineHeight: 1.2 }}>
              Talent Rankings
            </Typography>
            <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150, 10%, 46%)", mt: "6px" }}>
              Climb the leaderboard and earn your place among the top performers.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {period === "custom" && (
              <DateRangeSelect
                startDate={customRange.startDate}
                endDate={customRange.endDate}
                onChange={(range) => setCustomRange(range)}
              />
            )}
            <SegmentedToggle options={activeTab.periods} value={period} onChange={setPeriod} reduce={reduce} />
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: "4px", borderBottom: "1px solid hsl(140, 8%, 88%)" }}>
          {TABS.map((tab) => (
            <Box
              key={tab.key}
              component="button"
              onClick={() => selectTab(tab)}
              sx={{
                all: "unset",
                px: "4px",
                py: "10px",
                mr: "18px",
                fontSize: 14,
                fontFamily: tab.key === activeTabKey ? "Segoe UI Semibold, sans-serif, system-ui" : "Segoe UI Regular, sans-serif, system-ui",
                cursor: "pointer",
                color: tab.key === activeTabKey ? "hsl(150, 30%, 12%)" : "hsl(150, 10%, 46%)",
                borderBottom: `2px solid ${tab.key === activeTabKey ? "hsl(150, 38%, 17%)" : "transparent"}`,
                mb: "-1px",
                "&:hover": { color: "hsl(150, 30%, 12%)" },
              }}
            >
              {tab.label}
            </Box>
          ))}
        </Box>

        {loading && !data ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
            <CircularProgress size={28} sx={{ color: "hsl(150, 38%, 17%)" }} />
          </Box>
        ) : (
          <Box
            key={activeTabKey}
            component={motion.div}
            variants={containerVariants}
            initial="hidden"
            animate="show"
            sx={{ display: "flex", flexDirection: "column", gap: "22px" }}
          >
            {activeTabKey === "hall_of_fame" && (
              <>
                <Box component={motion.div} variants={sectionVariants}>
                  <Podium key={`podium-${period}`} entries={data?.podium || []} />
                </Box>
                <Box component={motion.div} variants={sectionVariants}>
                  <ChampionsWall key={`champions-${period}`} champions={data?.champions || []} />
                </Box>
                <Box component={motion.div} variants={sectionVariants}>
                  <SectionLabel>All-time leaderboard</SectionLabel>
                  <InternalUsersTable rows={leaderboardRows} showRank currentUserId={currentUserId} onUserClick={setSelectedUserId} />
                </Box>
              </>
            )}

            {activeTabKey === "top_officer" && (
              <>
                <Box component={motion.div} variants={sectionVariants}>
                  <TugOfWar key={`tow-${period}`} contenders={data?.contenders || []} currentUserId={currentUserId} periodLabel={PERIOD_LABELS[period]} />
                </Box>
                <Box component={motion.div} variants={sectionVariants}>
                  <SectionLabel>Everyone&apos;s activity</SectionLabel>
                  <InternalUsersTable rows={leaderboardRows} showRank currentUserId={currentUserId} onUserClick={setSelectedUserId} />
                </Box>
              </>
            )}

            {activeTabKey === "progress" && (
              <>
                <Box component={motion.div} variants={sectionVariants}>
                  <SectionLabel>Everyone&apos;s activity</SectionLabel>
                  <InternalUsersTable rows={leaderboardRows} currentUserId={currentUserId} onUserClick={setSelectedUserId} maxVisible={leaderboardRows.length} />
                </Box>
                <Box component={motion.div} variants={sectionVariants}>
                  <SectionLabel>My progress</SectionLabel>
                  <UserChartsSection userId={currentUserId} period={period} startDate={customRange.startDate} endDate={customRange.endDate} columns={2} chartHeight={240} extended />
                </Box>
              </>
            )}
          </Box>
        )}
      </Box>

      <InternalUserDrawer
        selectedUserId={selectedUserId}
        period={period}
        startDate={customRange.startDate}
        endDate={customRange.endDate}
        endpointBase="/internal/talent_rankings"
        allowArchive={false}
        onClose={() => setSelectedUserId(null)}
      />
    </Box>
  );
};

export default TalentRankingsPage;
